import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDashboardData } from "@/hooks/dashboard";
import getLink, { getDefaultLink, LINK_TYPE } from "@/lib/links";
import {
  COLLECTION_PERMISSIONS,
  iCollection,
  onBoardingCompleted,
  onBoardingLastStep,
} from "@/services/collection";
import { iGroup } from "@/services/groups";
import { ChevronDown, ChevronUp, CirclePlus, Group, Hotel } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Skeleton } from "../ui/skeleton";
import { useRbac } from "@/hooks/rbac";
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { useModal } from "@/context/ModalContext";
import AddCollectionForm from "@/pages/collection/onboarding/AddCollectionForm";
import { useAuthContext } from "@/context/AuthContext";

interface CollectionSelectorProps {
  collection: iCollection;
  className?: string;
}

export default function CollectionSelector({
  collection,
  className,
}: CollectionSelectorProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { can } = useRbac();
  const { showModal } = useModal();
  const { userEmail } = useAuthContext();

  const { groups, collectionsGroupedByGroupId, isLoading } = useDashboardData();
  const collectionFavicon = `https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${collection.website || "https://dairect.io"}&size=256`;

  const handleCollectionClick = (collection: iCollection) => {
    const onboardingComplete = onBoardingCompleted(collection);

    if (!onboardingComplete && can(COLLECTION_PERMISSIONS.update, collection)) {
      const lastStep = onBoardingLastStep(collection);
      navigate(`/collection/${collection._id}/infos/${lastStep}`);
    } else {
      const link = getDefaultLink(collection);
      navigate(link);
    }
  };

  return isLoading ? (
    <Skeleton className="h-12 w-[200px]" />
  ) : (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild className="w-full min-w-[200px]">
        <div
          className={`flex min-h-12 min-w-[200px] cursor-pointer items-center gap-2  p-4 ${
            className || ""
          }`}
        >
          {collectionFavicon ? (
            <img
              src={collectionFavicon}
              alt=""
              className="size-4 shrink-0 rounded-[4px]"
            />
          ) : null}
          <p className="min-w-0 flex-1 truncate text-[14px] font-semibold leading-4 tracking-[-0.28px] text-[#171717]">
            {collection
              ? collection.name
              : t("collection_selector_placeholder")}
          </p>
          {open ? (
            <ChevronUp className="h-4 w-4 shrink-0 text-[#171717]" />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0 text-[#171717]" />
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="max-h-[300px] overflow-y-auto">
        <DropdownMenuItem onClick={() => navigate(getLink(LINK_TYPE.HOME))}>
          <Group size={18} className="text-gray-400 mr-2" />
          <p className="text-sm text font-medium">{t("collection_view_all")}</p>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {groups.map((group: iGroup, index: number) => {
          const collections = collectionsGroupedByGroupId[group._id] || [];

          return (
            <div key={group._id}>
              {index > 0 && <DropdownMenuSeparator />}
              <DropdownMenuLabel>{group.name}</DropdownMenuLabel>
              {collections.map((collection: iCollection) =>
                !can(COLLECTION_PERMISSIONS.update, collection) &&
                !onBoardingCompleted(collection) ? (
                  //!onBoardingCompleted ? (
                  <TooltipProvider key={collection._id}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuItem key={collection._id}>
                          <Hotel size={18} className="text-gray-400 mr-2" />
                          <p className="text-sm text text-gray-400">
                            {collection.name}
                          </p>
                        </DropdownMenuItem>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t("tooltips.collection_missing_permission")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <DropdownMenuItem
                    key={collection._id}
                    onClick={() => handleCollectionClick(collection)}
                  >
                    <Hotel size={18} className="text-gray-400 mr-2" />
                    <p className="text-sm text">{collection.name}</p>
                  </DropdownMenuItem>
                ),
              )}
            </div>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            showModal(
              <AddCollectionForm
                collection={collection}
                userEmail={userEmail}
              />,
              {
                title: t("collection_add_collection"),
                description: "",
              },
            );
          }}
        >
          <CirclePlus className="mr-2" />
          <p className="text-sm text">{t("collection_add_collection")}</p>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
